import { Container, Text, TextStyle, Graphics, Sprite, Assets } from 'pixi.js';
import gsap from 'gsap';
import { BaseScene } from './BaseScene';
import { BoardContainer } from '../components/BoardContainer';
import { WarriorCardSprite } from '../components/WarriorCardSprite';
import { WaitingOverlay } from '../components/WaitingOverlay';
import { createViewState, type ViewState } from '../ViewState';
import { COLORS, FONTS, SPACING } from '../constants/theme';
import { BoardManager } from '@core/BoardManager';
import type { LayoutMetrics } from '../LayoutManager';
import type { GameState, BoardPosition, AvailableActions } from '@/types';
import type { NetworkController, NetworkRole } from '@network/NetworkController';

export interface GameBoardSceneOptions {
  getState: () => GameState;
  getNetworkRole?: () => NetworkRole | null;
  getNetworkController?: () => NetworkController | null;
  onWarriorSelect: (instanceId: string) => void;
  onMove: (position: BoardPosition) => void;
  onDash: (position: BoardPosition) => void;
  onAttack: () => void;
  onHeal?: () => void;
}

const CARD_GAP = 8;

/**
 * Main game board scene during battle phase
 */
export class GameBoardScene extends BaseScene {
  private metrics: LayoutMetrics;
  private options: GameBoardSceneOptions;
  private backgroundSprite: Sprite | null = null;
  private rootLayout: Container | null = null;
  private boardContainer: BoardContainer | null = null;

  // View state for local/remote abstraction
  private viewState: ViewState | null = null;

  // HUDs - local always at bottom/left, remote always at top/right
  private localHUD: Container | null = null;
  private remoteHUD: Container | null = null;
  private localCards: Map<string, WarriorCardSprite> = new Map();
  private remoteCards: Map<string, WarriorCardSprite> = new Map();
  private localNameContainer: Container | null = null;
  private remoteNameContainer: Container | null = null;

  private previewedEnemyId: string | null = null;
  private aiThinkingIndicator: Container | null = null;
  private isAIThinking: boolean = false;
  private remoteWaitingOverlay: WaitingOverlay | null = null;
  private turnCounterText: Text | null = null;
  private turnBadge: Container | null = null;
  private _adjustedCardWidth: number = 0;
  private _adjustedCardHeight: number = 0;

  constructor(metrics: LayoutMetrics, options: GameBoardSceneOptions) {
    super();
    this.metrics = metrics;
    this.options = options;
    this.build();
  }

  protected build(): void {
    // Will be built on enter when we have state
  }

  private async loadBackground(): Promise<void> {
    try {
      const backgroundTexture = await Assets.load('/assets/background.png');
      this.backgroundSprite = new Sprite(backgroundTexture);
      this.backgroundSprite.anchor.set(0, 0);
    } catch (error) {
      console.error('Failed to load background:', error);
    }
  }

  // Estimated label height for reserving space (label is rotated 90° so height becomes horizontal space)
  private static readonly LABEL_RESERVED_SPACE = 48; // Approximate space for rotated label + padding

  private buildBoard(): void {
    const state = this.options.getState();
    const networkRole = this.options.getNetworkRole?.() ?? null;
    const { mode, boardSize, width, height } = this.metrics;
    let { cardWidth, cardHeight } = this.metrics;
    const isPortrait = mode === 'portrait';

    // Create view state - this abstracts local vs remote players
    this.viewState = createViewState(state, networkRole);

    // Clear existing
    this.container.removeChildren();
    this.localCards.clear();
    this.remoteCards.clear();

    // In portrait mode, shrink cards to make room for name labels on both sides
    if (isPortrait) {
      const labelSpace = GameBoardScene.LABEL_RESERVED_SPACE * 2; // Both sides
      const availableWidth = boardSize - labelSpace; // Board width minus label space
      const maxCardWidth = (availableWidth - CARD_GAP * 2) / 3; // 3 cards with 2 gaps
      if (maxCardWidth < cardWidth) {
        const scale = maxCardWidth / cardWidth;
        cardWidth = maxCardWidth;
        cardHeight = cardHeight * scale;
      }
    }

    // Store adjusted dimensions for use in other methods
    this._adjustedCardWidth = cardWidth;
    this._adjustedCardHeight = cardHeight;

    // Background - stretched to fill screen
    if (this.backgroundSprite) {
      this.backgroundSprite.width = width;
      this.backgroundSprite.height = height;
      this.container.addChild(this.backgroundSprite);
    }

    // Root layout container - switches between row (landscape) and column (portrait)
    this.rootLayout = new Container({
      layout: {
        width,
        height,
        flexDirection: isPortrait ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: SPACING.MD,
      },
    });
    this.container.addChild(this.rootLayout);

    // Create HUD containers with flexbox layout
    // Local HUD - always at bottom (portrait) or left (landscape)
    this.localHUD = new Container({
      layout: {
        flexDirection: isPortrait ? 'row' : 'column',
        gap: CARD_GAP,
        alignItems: 'center',
      },
    });

    // Remote HUD - always at top (portrait) or right (landscape)
    this.remoteHUD = new Container({
      layout: {
        flexDirection: isPortrait ? 'row' : 'column',
        gap: CARD_GAP,
        alignItems: 'center',
      },
    });

    // Board wrapper for fixed size
    const boardWrapper = new Container({
      layout: {
        width: boardSize,
        height: boardSize,
      },
    });

    // Board container - uses view players for determining sprite rotation
    this.boardContainer = new BoardContainer({
      metrics: this.metrics,
      onTileClick: (pos) => this.handleTileClick(pos),
      onAttack: this.options.onAttack,
      onHeal: this.options.onHeal,
      viewPlayers: this.viewState.viewPlayers,
    });
    boardWrapper.addChild(this.boardContainer);

    // Layout is now simple: Remote HUD (top) -> Board -> Local HUD (bottom)
    // No more flipPerspective conditionals!
    if (isPortrait) {
      // Portrait: Remote HUD (top, rotated) -> Board -> Local HUD (bottom)
      this.remoteHUD.rotation = Math.PI;
      this.localHUD.rotation = 0;
      this.rootLayout.addChild(this.remoteHUD);
      this.rootLayout.addChild(boardWrapper);
      this.rootLayout.addChild(this.localHUD);
    } else {
      // Landscape: Local HUD (left) -> Board -> Remote HUD (right)
      // In 2-player mode, rotate remote HUD so Player 2 can read from the other side
      if (state.gameMode === '2-player') {
        this.remoteHUD.rotation = Math.PI;
      }
      this.rootLayout.addChild(this.localHUD);
      this.rootLayout.addChild(boardWrapper);
      this.rootLayout.addChild(this.remoteHUD);
    }

    // Build warrior cards using view state
    this.buildWarriorCards(cardWidth, cardHeight);

    // Create styled name labels - local uses PLAYER1 color, remote uses PLAYER2 color
    this.localNameContainer = this.createNameLabel(
      this.viewState.viewPlayers.local.name,
      COLORS.PLAYER1
    );
    this.remoteNameContainer = this.createNameLabel(
      this.viewState.viewPlayers.remote.name,
      COLORS.PLAYER2
    );

    // Add name labels to main scene container (rendered on top of everything except turn badge)
    this.container.addChild(this.localNameContainer);
    this.container.addChild(this.remoteNameContainer);

    // Create turn counter
    this.createTurnCounter();

    // Create AI thinking indicator
    this.createAIThinkingIndicator();

    // Initial state update (remote waiting overlay is created on-demand in updateState)
    this.updateState(state);
  }

  private createTurnCounter(): void {
    // Remove existing turn counter
    if (this.turnBadge) {
      this.container.removeChild(this.turnBadge);
      this.turnBadge = null;
      this.turnCounterText = null;
    }

    this.turnBadge = new Container();

    const textStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.SM,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });

    this.turnCounterText = new Text({ text: '1', style: textStyle });
    this.turnCounterText.anchor.set(0.5);

    // Create circular bordered background
    const radius = 16;

    const bg = new Graphics();
    bg.circle(0, 0, radius);
    bg.fill({ color: COLORS.BG_SECONDARY, alpha: 0.9 });
    bg.stroke({ color: COLORS.TEXT_SECONDARY, width: 2 });

    this.turnBadge.addChild(bg);
    this.turnBadge.addChild(this.turnCounterText);

    // Will be positioned in updateState based on current player
    this.container.addChild(this.turnBadge);
  }

  private createAIThinkingIndicator(): void {
    // Remove existing indicator
    if (this.aiThinkingIndicator) {
      this.container.removeChild(this.aiThinkingIndicator);
      this.aiThinkingIndicator = null;
    }

    const indicator = new Container();

    // Semi-transparent background overlay
    const overlay = new Graphics();
    overlay.rect(0, 0, this.metrics.width, this.metrics.height);
    overlay.fill({ color: 0x000000, alpha: 0.3 });
    indicator.addChild(overlay);

    // Thinking box - made taller for progress info
    const boxWidth = 280;
    const boxHeight = 100;
    const centerX = this.metrics.width / 2;
    const centerY = this.metrics.height / 2;

    const box = new Graphics();
    box.roundRect(centerX - boxWidth / 2, centerY - boxHeight / 2, boxWidth, boxHeight, 8);
    box.fill({ color: COLORS.BG_SECONDARY, alpha: 0.95 });
    box.stroke({ color: COLORS.PLAYER2, width: 2 });
    indicator.addChild(box);

    // Thinking text
    const textStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.MD,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const text = new Text({ text: 'AI is thinking...', style: textStyle });
    text.anchor.set(0.5);
    text.x = centerX;
    text.y = centerY - 25;
    indicator.addChild(text);

    // Progress text (smaller, shows what's being evaluated)
    const progressTextStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.SM,
      fill: COLORS.TEXT_SECONDARY,
    });
    const progressText = new Text({ text: '', style: progressTextStyle });
    progressText.anchor.set(0.5);
    progressText.x = centerX;
    progressText.y = centerY + 5;
    indicator.addChild(progressText);
    (indicator as any).progressText = progressText;

    // Progress bar background
    const barWidth = boxWidth - 40;
    const barHeight = 8;
    const barX = centerX - barWidth / 2;
    const barY = centerY + 28;

    const barBg = new Graphics();
    barBg.roundRect(barX, barY, barWidth, barHeight, 4);
    barBg.fill({ color: 0x1a1a2e });
    indicator.addChild(barBg);

    // Progress bar fill
    const barFill = new Graphics();
    barFill.roundRect(barX, barY, 0, barHeight, 4);
    barFill.fill({ color: COLORS.PLAYER2 });
    indicator.addChild(barFill);
    (indicator as any).progressBar = barFill;
    (indicator as any).barX = barX;
    (indicator as any).barY = barY;
    (indicator as any).barWidth = barWidth;
    (indicator as any).barHeight = barHeight;

    // Animate the dots
    let dotCount = 0;
    const animateDots = () => {
      if (!this.isAIThinking) return;
      dotCount = (dotCount + 1) % 4;
      text.text = 'AI is thinking' + '.'.repeat(dotCount);
      setTimeout(animateDots, 400);
    };
    (indicator as any).startAnimation = animateDots;

    indicator.visible = false;
    this.container.addChild(indicator);
    this.aiThinkingIndicator = indicator;
  }

  /**
   * Show the AI thinking indicator
   */
  showAIThinking(): void {
    this.isAIThinking = true;
    if (this.aiThinkingIndicator) {
      this.aiThinkingIndicator.visible = true;
      this.aiThinkingIndicator.alpha = 0;
      gsap.to(this.aiThinkingIndicator, { alpha: 1, duration: 0.2 });
      (this.aiThinkingIndicator as any).startAnimation?.();
    }
  }

  /**
   * Hide the AI thinking indicator
   */
  hideAIThinking(): void {
    this.isAIThinking = false;
    if (this.aiThinkingIndicator) {
      gsap.to(this.aiThinkingIndicator, {
        alpha: 0,
        duration: 0.2,
        onComplete: () => {
          if (this.aiThinkingIndicator) {
            this.aiThinkingIndicator.visible = false;
          }
        },
      });
    }
  }

  /**
   * Update the AI thinking progress
   */
  updateAIThinkingProgress(progress: { currentMove: number; totalMoves: number; currentWarrior: string; currentAction: string }): void {
    if (!this.aiThinkingIndicator) return;

    const indicator = this.aiThinkingIndicator as any;

    // Update progress text
    if (indicator.progressText) {
      indicator.progressText.text = `${progress.currentWarrior}: ${progress.currentAction}`;
    }

    // Update progress bar
    if (indicator.progressBar) {
      const percentage = progress.currentMove / progress.totalMoves;
      const fillWidth = indicator.barWidth * percentage;

      indicator.progressBar.clear();
      indicator.progressBar.roundRect(indicator.barX, indicator.barY, fillWidth, indicator.barHeight, 4);
      indicator.progressBar.fill({ color: COLORS.PLAYER2 });
    }
  }

  /**
   * Show the remote player waiting overlay
   */
  private showRemoteWaiting(): void {
    if (this.remoteWaitingOverlay) return; // Already showing

    this.remoteWaitingOverlay = new WaitingOverlay(this.metrics, {
      title: "Opponent's Turn",
      message: 'Waiting for opponent to move...',
    });
    this.container.addChild(this.remoteWaitingOverlay);
  }

  /**
   * Hide the remote player waiting overlay
   */
  private hideRemoteWaiting(): void {
    if (this.remoteWaitingOverlay) {
      this.remoteWaitingOverlay.hide();
      this.remoteWaitingOverlay = null;
    }
  }

  private buildWarriorCards(cardWidth?: number, cardHeight?: number): void {
    if (!this.viewState) return;

    const width = cardWidth ?? this.metrics.cardWidth;
    const height = cardHeight ?? this.metrics.cardHeight;
    const state = this.options.getState();
    const isOnline = state.gameMode === 'online';

    // Local player cards - always at bottom/left
    for (const warrior of this.viewState.localWarriors) {
      const card = new WarriorCardSprite({
        warrior,
        width,
        height,
        isSelected: state.selectedWarrior === warrior.instanceId,
        isCurrentTurn: this.viewState.isLocalTurn,
        isRotated: false,
        onSelect: (id) => {
          const currentState = this.options.getState();
          // Can select if it's this warrior's owner's turn
          if (warrior.ownerId === currentState.currentTurn) {
            this.previewedEnemyId = null;
            this.options.onWarriorSelect(id);
          }
        },
        onAttack: this.options.onAttack,
        onHeal: this.options.onHeal,
      });

      card.layout = { width, height };
      this.localHUD!.addChild(card);
      this.localCards.set(warrior.instanceId, card);
    }

    // Remote player cards - always at top/right
    for (const warrior of this.viewState.remoteWarriors) {
      const card = new WarriorCardSprite({
        warrior,
        width,
        height,
        isSelected: state.selectedWarrior === warrior.instanceId,
        isCurrentTurn: !this.viewState.isLocalTurn,
        isRotated: false, // HUD rotation handles orientation
        onSelect: (id) => {
          const currentState = this.options.getState();
          // In 2-player mode, allow selection if it's this warrior's owner's turn
          if (warrior.ownerId === currentState.currentTurn) {
            this.previewedEnemyId = null;
            this.options.onWarriorSelect(id);
          } else {
            // Not their turn - toggle attack preview
            this.previewedEnemyId = this.previewedEnemyId === id ? null : id;
            this.updateState(currentState);
          }
        },
        // In online mode, no attack/heal buttons on remote cards
        onAttack: isOnline ? undefined : this.options.onAttack,
        onHeal: isOnline ? undefined : this.options.onHeal,
      });

      card.layout = { width, height };
      this.remoteHUD!.addChild(card);
      this.remoteCards.set(warrior.instanceId, card);
    }
  }

  private createNameLabel(name: string, color: number): Container {
    const container = new Container();

    // Create text first to measure it
    const textStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: FONTS.SIZES.MD,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const text = new Text({ text: name, style: textStyle });
    text.anchor.set(0.5);

    // Scale down text if it's too wide
    const maxTextWidth = 100;
    if (text.width > maxTextWidth) {
      text.scale.set(maxTextWidth / text.width);
    }

    // Background with border - fully opaque
    const paddingX = 16;
    const paddingY = 8;
    const bgWidth = text.width * text.scale.x + paddingX * 2;
    const bgHeight = text.height * text.scale.y + paddingY * 2;

    const bg = new Graphics();
    bg.roundRect(-bgWidth / 2, -bgHeight / 2, bgWidth, bgHeight, 6);
    bg.fill({ color: COLORS.BG_SECONDARY }); // Fully opaque background
    bg.stroke({ color, width: 2 });

    container.addChild(bg);
    container.addChild(text);

    // Store dimensions for positioning
    (container as any)._labelWidth = bgWidth;
    (container as any)._labelHeight = bgHeight;

    return container;
  }

  private positionNameLabels(): void {
    const { mode, width, height, boardSize } = this.metrics;
    const isPortrait = mode === 'portrait';

    // Use adjusted card dimensions in portrait mode
    const cardWidth = this._adjustedCardWidth || this.metrics.cardWidth;
    const cardHeight = this._adjustedCardHeight || this.metrics.cardHeight;

    if (isPortrait) {
      // Calculate actual flexbox positions (flexbox uses SPACING.MD gap, centered)
      const gap = SPACING.MD;
      const hudHeight = cardHeight; // In portrait, cards are in a row, so HUD height = card height
      const totalContentHeight = hudHeight + gap + boardSize + gap + hudHeight;
      const contentStartY = (height - totalContentHeight) / 2;

      // Calculate HUD horizontal position (cards are centered)
      const totalHudWidth = cardWidth * 3 + CARD_GAP * 2;
      const hudStartX = (width - totalHudWidth) / 2;

      // Actual Y positions from flexbox layout
      // Remote HUD is at top, Local HUD is at bottom
      const topHudTop = contentStartY;
      const topHudCenterY = topHudTop + hudHeight / 2;
      const bottomHudTop = contentStartY + hudHeight + gap + boardSize + gap;
      const bottomHudCenterY = bottomHudTop + hudHeight / 2;

      // Badge dimensions for centering the name+badge combo
      const badgeRadius = 16;
      const badgeGap = 6;

      // Local name: to the left of local HUD (bottom), rotated 90 degrees CCW
      if (this.localNameContainer) {
        const labelHeight = (this.localNameContainer as any)._labelHeight || 32;
        const nameOffsetY = (badgeRadius * 2 + badgeGap) / 2;

        this.localNameContainer.x = hudStartX - SPACING.SM - labelHeight / 2;
        this.localNameContainer.y = bottomHudCenterY + nameOffsetY;
        this.localNameContainer.rotation = -Math.PI / 2; // CCW
      }

      // Remote name: to the right of remote HUD (top), rotated 90 degrees CW
      if (this.remoteNameContainer) {
        const labelHeight = (this.remoteNameContainer as any)._labelHeight || 32;
        const nameOffsetY = -(badgeRadius * 2 + badgeGap) / 2;

        this.remoteNameContainer.x = hudStartX + totalHudWidth + SPACING.SM + labelHeight / 2;
        this.remoteNameContainer.y = topHudCenterY + nameOffsetY;
        this.remoteNameContainer.rotation = Math.PI / 2; // CW
      }
    } else {
      // Landscape: Local on left, Remote on right
      // Use the pre-calculated HUD positions from metrics
      // Local HUD uses player1HUD position, Remote HUD uses player2HUD position
      const { player1HUD, player2HUD } = this.metrics;

      if (this.localNameContainer) {
        this.localNameContainer.x = player1HUD.x + player1HUD.width / 2;
        this.localNameContainer.y = SPACING.MD + 16;
        this.localNameContainer.rotation = 0;
      }
      if (this.remoteNameContainer) {
        this.remoteNameContainer.x = player2HUD.x + player2HUD.width / 2;
        this.remoteNameContainer.y = SPACING.MD + 16;
        this.remoteNameContainer.rotation = 0;
      }
    }
  }

  private handleTileClick(position: BoardPosition): void {
    // Disable interactions during AI turn
    if (this.isAIThinking) return;
    if (!this.viewState) return;

    const state = this.options.getState();

    // Disable if it's AI's turn (1-player mode and not local turn)
    if (state.gameMode === '1-player' && !this.viewState.isLocalTurn) return;

    // Disable if it's remote player's turn in online mode
    if (state.gameMode === 'online' && !this.viewState.isLocalTurn) return;

    const allWarriors = this.viewState.allWarriors;

    // Get available actions
    const selectedWarrior = state.selectedWarrior
      ? allWarriors.find((w) => w.instanceId === state.selectedWarrior)
      : null;

    const availableActions = selectedWarrior
      ? BoardManager.getAvailableActions(selectedWarrior, state.board, [...allWarriors])
      : null;

    // Check if clicking on a warrior
    const warriorAtPosition = allWarriors.find(
      (w) => w.position?.row === position.row && w.position?.col === position.col
    );

    if (warriorAtPosition) {
      // Check if this warrior belongs to the current turn's player
      const isCurrentPlayersWarrior = warriorAtPosition.ownerId === state.currentTurn;

      if (isCurrentPlayersWarrior) {
        // Clicking current player's warrior on their turn: select it
        this.previewedEnemyId = null;
        this.options.onWarriorSelect(warriorAtPosition.instanceId);
        return;
      } else {
        // Clicking remote/enemy warrior: check if we can attack them
        if (availableActions?.canAttack && warriorAtPosition.position) {
          const isInAttackRange = availableActions.attackTargetPositions.some(
            (p) => p.row === warriorAtPosition.position!.row && p.col === warriorAtPosition.position!.col
          );
          if (isInAttackRange) {
            // Trigger attack
            this.previewedEnemyId = null;
            this.options.onAttack();
            return;
          }
        }
        // Not in attack range: toggle attack preview
        this.previewedEnemyId =
          this.previewedEnemyId === warriorAtPosition.instanceId
            ? null
            : warriorAtPosition.instanceId;
        this.updateState(state);
        return;
      }
    }

    // Clear enemy preview when clicking elsewhere
    if (this.previewedEnemyId) {
      this.previewedEnemyId = null;
    }

    if (!availableActions) return;

    // Check if clicking a move position
    const isMovePosition = availableActions.validMovePositions.some(
      (p) => p.row === position.row && p.col === position.col
    );
    if (isMovePosition) {
      this.options.onMove(position);
      return;
    }

    // Check if clicking a dash position
    const isDashPosition = availableActions.validDashPositions.some(
      (p) => p.row === position.row && p.col === position.col
    );
    if (isDashPosition) {
      this.options.onDash(position);
      return;
    }
  }

  /**
   * Update the scene with new game state
   */
  updateState(state: GameState): void {
    if (!this.boardContainer) return;

    // Update view state
    const networkRole = this.options.getNetworkRole?.() ?? null;
    this.viewState = createViewState(state, networkRole);

    // Update board container's view players
    this.boardContainer.setViewPlayers(this.viewState.viewPlayers);

    const allWarriors = [...this.viewState.allWarriors];

    // Get selected warrior and available actions
    const selectedWarrior = state.selectedWarrior
      ? allWarriors.find((w) => w.instanceId === state.selectedWarrior)
      : null;

    let availableActions: AvailableActions = {
      canMove: false,
      validMovePositions: [],
      canDash: false,
      validDashPositions: [],
      canAttack: false,
      attackTargetPositions: [],
      canHeal: false,
      healTargetPositions: [],
    };

    if (selectedWarrior) {
      availableActions = BoardManager.getAvailableActions(selectedWarrior, state.board, allWarriors);
    }

    // Calculate position arrays
    let movePositions: BoardPosition[] = [];
    let dashPositions: BoardPosition[] = [];
    let attackPositions: BoardPosition[] = [];
    let previewAttackPositions: BoardPosition[] = [];

    if (selectedWarrior?.position) {
      if (availableActions.canMove) {
        movePositions = [...availableActions.validMovePositions];
      }
      if (availableActions.canDash) {
        dashPositions = [...availableActions.validDashPositions];
      }

      // Always show attack range for selected warrior
      // Use viewPlayers to determine if this is the remote player (for pattern mirroring)
      const isRemote = selectedWarrior.ownerId === this.viewState.viewPlayers.remoteId;
      attackPositions = BoardManager.getAttackTargetPositions(
        selectedWarrior.position,
        selectedWarrior.definition.attackPattern,
        isRemote
      );
    }

    // Handle enemy preview
    if (this.previewedEnemyId) {
      const previewedWarrior = allWarriors.find((w) => w.instanceId === this.previewedEnemyId);
      if (previewedWarrior?.position) {
        const isRemote = previewedWarrior.ownerId === this.viewState.viewPlayers.remoteId;
        previewAttackPositions = BoardManager.getAttackTargetPositions(
          previewedWarrior.position,
          previewedWarrior.definition.attackPattern,
          isRemote
        );
      }
    }

    // Update board
    this.boardContainer.updateState(
      allWarriors,
      state.selectedWarrior,
      movePositions,
      dashPositions,
      attackPositions,
      previewAttackPositions
    );

    // Update HUD opacity based on local turn
    if (this.localHUD) {
      this.localHUD.alpha = this.viewState.isLocalTurn ? 1 : 0.5;
    }
    if (this.remoteHUD) {
      this.remoteHUD.alpha = this.viewState.isLocalTurn ? 0.5 : 1;
    }

    // Update name label opacity to match HUD
    if (this.localNameContainer) {
      this.localNameContainer.alpha = this.viewState.isLocalTurn ? 1 : 0.5;
    }
    if (this.remoteNameContainer) {
      this.remoteNameContainer.alpha = this.viewState.isLocalTurn ? 0.5 : 1;
    }

    // Position name labels above their respective HUDs
    this.positionNameLabels();

    // Update turn counter badge - position next to active player's name (local when local turn)
    if (this.turnBadge && this.turnCounterText) {
      this.turnCounterText.text = String(state.turnNumber);

      // Badge goes next to whoever's turn it is
      const activeNameContainer = this.viewState.isLocalTurn ? this.localNameContainer : this.remoteNameContainer;
      if (activeNameContainer) {
        const labelWidth = (activeNameContainer as any)._labelWidth || 80;
        const badgeRadius = 16;
        const gap = 6;

        if (this.metrics.mode === 'portrait') {
          // In portrait, labels are rotated 90 degrees
          if (this.viewState.isLocalTurn) {
            // Local label is rotated CCW (-90°), badge is above (negative Y)
            this.turnBadge.x = activeNameContainer.x;
            this.turnBadge.y = activeNameContainer.y - labelWidth / 2 - gap - badgeRadius;
            this.turnBadge.rotation = 0;
          } else {
            // Remote label is rotated CW (90°), badge is below (positive Y), rotated 180°
            this.turnBadge.x = activeNameContainer.x;
            this.turnBadge.y = activeNameContainer.y + labelWidth / 2 + gap + badgeRadius;
            this.turnBadge.rotation = Math.PI;
          }
        } else {
          // Landscape: Badge positioned to the right of the name label
          this.turnBadge.x = activeNameContainer.x + labelWidth / 2 + gap + badgeRadius;
          this.turnBadge.y = activeNameContainer.y;
          this.turnBadge.rotation = 0;
        }
      }
    }

    // Update local warrior cards
    for (const warrior of this.viewState.localWarriors) {
      const card = this.localCards.get(warrior.instanceId);
      if (card) {
        const cardActions =
          this.viewState.isLocalTurn && state.selectedWarrior === warrior.instanceId
            ? availableActions
            : undefined;
        card.update(
          warrior,
          state.selectedWarrior === warrior.instanceId,
          this.viewState.isLocalTurn,
          cardActions
        );
      }
    }

    // Update remote warrior cards
    for (const warrior of this.viewState.remoteWarriors) {
      const card = this.remoteCards.get(warrior.instanceId);
      if (card) {
        const cardActions =
          !this.viewState.isLocalTurn && state.selectedWarrior === warrior.instanceId
            ? availableActions
            : undefined;
        card.update(
          warrior,
          state.selectedWarrior === warrior.instanceId,
          !this.viewState.isLocalTurn,
          cardActions
        );
      }
    }

    // Handle remote waiting overlay for online mode
    if (state.gameMode === 'online') {
      if (!this.viewState.isLocalTurn) {
        this.showRemoteWaiting();
      } else {
        this.hideRemoteWaiting();
      }
    }
  }

  async onEnter(): Promise<void> {
    super.onEnter();
    this.previewedEnemyId = null;

    // Load background first, then build board
    await this.loadBackground();
    this.buildBoard();

    // Fade in
    this.container.alpha = 0;
    gsap.to(this.container, { alpha: 1, duration: 0.3 });
  }

  onResize(metrics: LayoutMetrics): void {
    this.metrics = metrics;

    // Rebuild if layout mode changed
    if (this.isActive) {
      this.buildBoard();
    }
  }

  dispose(): void {
    this.hideRemoteWaiting();
    this.boardContainer?.dispose();
    super.dispose();
  }
}
